import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';

import classNames from 'classnames';
import { capitalize, debounce } from 'lodash';
import { useInterval } from 'usehooks-ts';

import { useGetOneInchTokenAllowanceQuery } from '@query';

import { GetOneInchGasPricesResponse, getOneInchQuote, OneInchNetwork, OneInchToken } from '@api';

import { useModalController } from '@uikit/components/Modal';
import Spinner from '@uikit/components/Spinner/Spinner';
import { AngleDouble } from '@uikit/icons/AngleDouble';
import { AngleUp } from '@uikit/icons/AngleUp';
import { Chain } from '@uikit/icons/Chain';
import { WalletOutline } from '@uikit/icons/WalletOutline';

import { ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS } from '@shared/constants/magicSwap';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { truncateDecimals } from '@shared/helpers/formatters';
import { numberToWei, weiToNumber } from '@shared/helpers/web3/formatters/numberToWei';
import { TokenBalance } from '@shared/helpers/web3/getUserTokenBalance';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import {
  DEFAULT_QUOTE_REFETCH_INTERVAL,
  MAX_BTN_CONTENT,
  MAX_BTN_CONTENT_LOW_BALANCE,
  MAX_DECIMALS,
  MIN_VALUE,
} from '@magic-swap/components/pages/MagicSwap/constants';
import useGetOneInchGasPrice from '@magic-swap/components/pages/MagicSwap/hooks/useGetOneInchGasPrice';
import useTokensPricesInUSD from '@magic-swap/components/pages/MagicSwap/hooks/useTokensPricesInUSD';
import { OneInchView, SwapState } from '@magic-swap/components/pages/MagicSwap/types';
import MagicSwapButton from '@magic-swap/components/pages/MagicSwap/ui/buttons/MagicSwapButton';
import SwapMaxBalanceButton from '@magic-swap/components/pages/MagicSwap/ui/buttons/SwapMaxBalanceButton';
import { TokenSelectButton } from '@magic-swap/components/pages/MagicSwap/ui/buttons/TokenSelectButton';
import SwapAmountInput from '@magic-swap/components/pages/MagicSwap/ui/inputs/SwapAmountInput';
import SlippageModal from '@magic-swap/components/pages/MagicSwap/ui/modals/SlippageModal';
import NetworkOption from '@magic-swap/components/pages/MagicSwap/ui/NetworkOption';
import { SuccessSwapTxInfo } from '@magic-swap/components/pages/MagicSwap/ui/views/MagicSwapSuccessView';
import SwapDetailsWidget from '@magic-swap/components/pages/MagicSwap/ui/widgets/SwapDetailsWidget';
import SwapKarmaRewardWidget from '@magic-swap/components/pages/MagicSwap/ui/widgets/SwapKarmaRewardWidget';
import calcTotalGasInGwei from '@magic-swap/components/pages/MagicSwap/utils/calcTotalGasInGwei';
import formatNumberForDisplay from '@magic-swap/components/pages/MagicSwap/utils/formatNumberForDisplay';

interface MagicSwapMainViewProps {
  setView: (view: OneInchView) => void;
  network?: OneInchNetwork;
  fromToken?: OneInchToken;
  toToken?: OneInchToken;
  stringFromAmount: string;
  isLoadingNetwork: boolean;
  isLoadingTokens: boolean;
  fromTokenBalance: TokenBalance;
  setFromAmount: (value: string) => void;
  setFromToken: (token: OneInchToken, isInvertAction?: boolean) => void;
  setToToken: (token: OneInchToken, isInvertAction?: boolean) => void;
  fetchFromTokenBalance: () => Promise<void>;
  fetchToTokenBalance: () => Promise<void>;
  tokens: OneInchToken[];
  toTokenBalance: TokenBalance;
  setSuccessTxInfo: (tx: SuccessSwapTxInfo) => void;
  setTxError: (e: Error) => void;
  slippage: number;
  setSlippage: (slippage: number) => void;
  quote?: Awaited<ReturnType<typeof getOneInchQuote>>;
  fromAmount: number;
  refetchQuote: () => Promise<void>;
  allowanceQuery: ReturnType<typeof useGetOneInchTokenAllowanceQuery>;
  isLoadingQuote: boolean;
  swapState: SwapState;
  setSwapState: Dispatch<SetStateAction<SwapState>>;
  gasPrices?: GetOneInchGasPricesResponse;
}

const formatForDisplayProps = {
  minValue: MIN_VALUE,
  maxDecimals: MAX_DECIMALS,
};

const MagicSwapMainView = ({
  quote,
  allowanceQuery,
  fromAmount,
  isLoadingQuote,
  refetchQuote,
  fetchToTokenBalance,
  setTxError,
  fetchFromTokenBalance,
  tokens,
  toTokenBalance,
  setFromToken,
  setToToken,
  fromTokenBalance,
  setFromAmount,
  isLoadingNetwork,
  isLoadingTokens,
  stringFromAmount,
  setView,
  network,
  fromToken,
  toToken,
  setSuccessTxInfo,
  slippage,
  setSlippage,
  swapState,
  setSwapState,
  gasPrices,
}: MagicSwapMainViewProps) => {
  const { userWallet, isAuthed } = useMagicIdWallet();

  const slippageModal = useModalController();
  const [inverted, setInverted] = useState(false);
  const [maxButtonTooltipContent, setMaxButtonTooltipContent] = useState('');

  const { getOneInchGasPrice } = useGetOneInchGasPrice({ gasPrices });

  const nativeToken = useMemo(() => {
    return tokens.find((t) => t.id === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS);
  }, [tokens]);

  useEffect(() => {
    if (quote && quote.error && quote.message) {
      const error = capitalize(quote.message);
      displayErrorToast({
        error,
        replaceErrorMessageCallback: (newMessage) => ({
          newMessage,
          toastOptions: {
            type: 'info',
          },
        }),
      });
    }
  }, [quote]);

  const { totalGasInTokens } = useMemo(() => {
    const gasAmount = quote?.data?.gas ?? 0;
    const gasPrice = getOneInchGasPrice();
    const totalGas = calcTotalGasInGwei({ gasAmount, gasPrice });
    const totalGasInTokensInner =
      totalGas && nativeToken ? weiToNumber(totalGas, nativeToken.attributes.decimal) : 0;

    return {
      gasAmount,
      gasPrice,
      totalGas,
      totalGasInTokens: totalGasInTokensInner < 0 ? 0 : totalGasInTokensInner,
    };
  }, [quote, nativeToken, getOneInchGasPrice]);

  const {
    fromTokenPriceInUSD,
    toTokenPriceInUSD,
    nativeTokenPriceInUSD,
    isLoadingToTokenPriceInUSD,
  } = useTokensPricesInUSD({
    network,
    fromToken,
    fromAmount,
    inverted,
    toToken,
    nativeToken,
  });

  const setMaxBalance = () => {
    setFromAmount(String(fromTokenBalance.balance ?? 0));
  };

  const putMaxTokenAmount = debounce(async () => {
    if (!fromToken || !network || !userWallet) {
      return false;
    }

    if (fromToken.id !== ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS) {
      return setMaxBalance();
    }

    const toTokenAddress =
      toToken?.id || tokens.find((t) => t.id !== ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS)?.id;

    if (!toTokenAddress) {
      return setMaxBalance();
    }

    const quoteResponse = await getOneInchQuote({
      searchParams: {
        fromTokenAddress: ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS,
        toTokenAddress,
        amount: fromTokenBalance.raw.value.toString(),
        networkId: network.id,
      },
    });

    const gasAmount = quoteResponse?.data?.gas ?? 0;
    const gasPrice = getOneInchGasPrice();

    if (!gasAmount || !gasPrice) {
      return setMaxBalance();
    }

    const totalGas = calcTotalGasInGwei({ gasAmount, gasPrice });
    const maxBalance = BigInt(fromTokenBalance.raw.value) - totalGas;
    const newAmount = weiToNumber(maxBalance < 0 ? 0 : maxBalance, fromToken.attributes.decimal);

    if (newAmount === 0) {
      setMaxButtonTooltipContent(MAX_BTN_CONTENT_LOW_BALANCE);
    } else {
      setMaxButtonTooltipContent(MAX_BTN_CONTENT);
    }

    setFromAmount(String(newAmount));
    setSwapState((prev) => ({
      ...prev,
      isUsingMax: true,
    }));

    setSwapState((prev) => ({ ...prev, maxAmountRefetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL }));
  }, 300);

  const putMaxEventWrapper = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    putMaxTokenAmount();
  };

  useInterval(
    () => putMaxTokenAmount(),
    swapState.isUsingMax && swapState.maxAmountRefetchInterval && fromAmount
      ? swapState.maxAmountRefetchInterval
      : null,
  );

  const tokensLoading = isLoadingNetwork || isLoadingTokens;

  const { toAmountInWei, toAmountInNum } = useMemo(() => {
    if (!quote?.data?.toAmount || !toToken?.attributes?.decimal || !fromAmount) {
      return {
        toAmountInWei: BigInt(0),
        toAmountInNum: 0,
      };
    }

    const toAmountInWeiInner = BigInt(quote?.data?.toAmount || '0');
    const formattedAmount = weiToNumber(toAmountInWeiInner, toToken.attributes.decimal);
    const toAmountInNumInner = MIN_VALUE < formattedAmount ? formattedAmount : 0;

    return {
      toAmountInWei: toAmountInWeiInner,
      toAmountInNum: toAmountInNumInner,
    };
  }, [quote, toToken, fromAmount]);

  const formattedFromTokenPriceInUSD =
    fromTokenPriceInUSD && fromAmount
      ? formatNumberForDisplay(fromTokenPriceInUSD * fromAmount, formatForDisplayProps)
      : 0;

  const toTokenPriceInUSDInWei =
    toTokenPriceInUSD && toToken
      ? numberToWei(toTokenPriceInUSD, toToken.attributes.decimal).toBigInt()
      : BigInt(0);
  const formattedToTokenPriceInUSD =
    toTokenPriceInUSDInWei && toToken && toAmountInWei
      ? weiToNumber(toTokenPriceInUSDInWei * toAmountInWei, toToken.attributes.decimal * 2)
      : 0;

  const invertTokens = debounce(() => {
    if (!toToken || !fromToken) {
      return;
    }

    const temp = fromToken;
    setFromToken(toToken, true);
    setToToken(temp, true);
    setFromAmount(String(toAmountInNum));
    setInverted((prev) => !prev);
    refetchQuote();
  }, 200);

  const getIsBalanceExceeded = () => {
    if (!isAuthed) {
      return false;
    }

    if (!fromAmount) {
      return false;
    }

    if (!fromTokenBalance) {
      return fromAmount > 0;
    }

    return numberToWei(fromAmount, fromTokenBalance.raw.decimals).gte(fromTokenBalance.raw.value);
  };

  const balanceExceeded = getIsBalanceExceeded();

  const providerFee = useMemo(() => {
    if (!nativeToken || !totalGasInTokens || !toAmountInWei) {
      return '0';
    }

    const totalGasInUSD = totalGasInTokens * nativeTokenPriceInUSD;

    const fix = (amount: number) => amount.toFixed(nativeToken.attributes.decimal);

    const fixedGasInTokens = fix(totalGasInTokens);
    const fixedGasInUSD = totalGasInUSD ? fix(totalGasInUSD) : null;

    const gasInTokens = fixedGasInTokens.startsWith('0.00000000')
      ? `<0.00000001`
      : `${truncateDecimals(fixedGasInTokens, 8)}`;

    const gasInUSD =
      !!fixedGasInUSD &&
      (fixedGasInUSD.startsWith('0.00000')
        ? ` (<$0.00001)`
        : ` ($${truncateDecimals(fixedGasInUSD, 5)})`);

    return `≈ ${gasInTokens} ${nativeToken.attributes.symbol}${gasInUSD || ''}`;
  }, [nativeToken, totalGasInTokens, nativeTokenPriceInUSD, toAmountInWei]);

  return (
    <>
      <h1 className="text-[28px] mb-8 font-semibold">Magic Swap</h1>

      <div
        className={classNames(
          'w-full rounded-2xl bg-primary-50 py-3 px-4 border-2 border-primary-50',
          {
            'border-red-600': balanceExceeded,
          },
        )}
      >
        <div className="flex justify-between items-center gap-1 mb-6 flex-wrap">
          <div className="flex items-center flex-grow">
            <p className="t-body2 text-neutral-400">From</p>

            <button
              className="flex gap-1 items-center text-sm font-light justify-start focus-visible:outline-none px-2 m-1 rounded-2xl"
              disabled={isLoadingNetwork}
              onClick={() => setView('networkSelect')}
            >
              {!network && <Chain className="icon" />}
              {isLoadingNetwork && <Spinner size="sm" color="secondary" />}
              {!isLoadingNetwork && network ? (
                <NetworkOption network={network} small />
              ) : (
                'Select Network'
              )}
              <AngleUp className="icon rotate-90 text-primary" />
            </button>
          </div>

          <div className="flex gap-1 items-center text-neutral-500">
            <WalletOutline className="icon-[24px]" />
            <p className="text-[16px] text-neutral-400 font-semibold max-sm:text-[14px]">
              {fromTokenBalance.balance || '0.0'}
            </p>

            <SwapMaxBalanceButton
              maxButtonTooltipContent={maxButtonTooltipContent}
              putMaxTokenAmount={putMaxEventWrapper}
              fromTokenBalance={fromTokenBalance}
            />
          </div>
        </div>

        <h3 className="t-body2 text-neutral-400 flex-grow">You pay</h3>

        <div className="flex gap-1 justify-between">
          <TokenSelectButton
            fetchTokenBalance={fetchFromTokenBalance}
            network={network}
            token={fromToken}
            tokens={tokens ?? []}
            setToken={setFromToken}
            isLoading={tokensLoading}
            onClick={() => setView('fromTokenSelect')}
          />

          <div className="flex flex-col w-1/2 items-end justify-end">
            <SwapAmountInput
              setFromAmount={setFromAmount}
              stringFromAmount={stringFromAmount}
              swapState={swapState}
              setSwapState={setSwapState}
            />

            <div className="flex gap-4 justify-end items-center">
              {balanceExceeded && <p className="t-body2 text-red-600">Balance Exceeded</p>}

              {!balanceExceeded && !!formattedFromTokenPriceInUSD && (
                <p className="t-body2 text-neutral-400">≈${formattedFromTokenPriceInUSD}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={invertTokens}
        disabled={!toToken || !fromToken}
        className="relative z-1 button button-sm duration-300 hover:enabled:rotate-180 rounded-full bg-primary-100 text-secondary p-3 max-sm:p-1.5 mx-auto my-[-16px] max-sm:my-[-8px] border-2 border-neutral-50"
      >
        <AngleDouble className="icon-[24px] max-sm:icon-[12px] rotate-90" />
      </button>

      <div className="w-full rounded-2xl bg-primary-50 py-3 px-4">
        <div className="flex justify-between items-center gap-1 mb-6">
          <p className="t-body2 text-neutral-400">To</p>

          <div className="flex gap-1 items-center text-neutral-500">
            <WalletOutline className="icon-[24px]" />

            <p className="text-[16px] text-neutral-400 font-semibold">
              {toTokenBalance.balance || '0.0'}
            </p>
          </div>
        </div>

        <h3 className="t-body2 text-neutral-400 flex-grow">You Receive</h3>

        <div className="flex gap-1 justify-between items-center">
          <TokenSelectButton
            fetchTokenBalance={fetchToTokenBalance}
            network={network}
            token={toToken}
            tokens={tokens ?? []}
            setToken={setToToken}
            isLoading={tokensLoading}
            onClick={() => setView('toTokenSelect')}
          />

          <div className="flex flex-col w-1/2 items-end justify-end">
            {isLoadingQuote ? (
              <Spinner size="sm" color="primary" />
            ) : (
              <NumericFormat
                className="my-2 t-h3 max-sm:text-[16px] bg-transparent outline-none text-right w-full overflow-auto"
                allowNegative={false}
                thousandSeparator
                readOnly
                decimalScale={MAX_DECIMALS}
                max={Number.MAX_SAFE_INTEGER}
                value={toAmountInNum}
                placeholder="0.00"
              />
            )}

            {!!formattedToTokenPriceInUSD && !isLoadingQuote && (
              <p className="t-body2 text-neutral-400">
                ≈${formatNumberForDisplay(formattedToTokenPriceInUSD, formatForDisplayProps)}
              </p>
            )}
          </div>
        </div>
      </div>

      <MagicSwapButton
        slippage={slippage}
        setSuccessTxInfo={setSuccessTxInfo}
        setTxError={setTxError}
        setView={setView}
        fromToken={fromToken}
        toToken={toToken}
        amount={fromAmount}
        balanceExceeded={balanceExceeded}
        isLoading={isLoadingNetwork || isLoadingTokens || isLoadingQuote}
        network={network}
        allowanceQuery={allowanceQuery}
      />

      <SwapDetailsWidget
        providerFee={providerFee}
        slippageModal={slippageModal}
        slippage={slippage}
      />

      <SwapKarmaRewardWidget
        formattedToTokenPriceInUSD={formattedToTokenPriceInUSD}
        isLoadingToTokenPriceInUSD={isLoadingToTokenPriceInUSD}
        isLoadingQuote={isLoadingQuote}
        fromToken={fromToken}
        toToken={toToken}
        balanceExceeded={balanceExceeded}
      />

      <SlippageModal
        modalController={slippageModal}
        setSlippage={setSlippage}
        slippage={slippage}
      />
    </>
  );
};

export default MagicSwapMainView;
